var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { backendUrl, validateGameImageSize } from '@common/contracts';
// Resolve a stored image reference to a loadable URL. Node/art images are stored as
// RELATIVE `/uploads/<id>` paths; the backend serves them on its own origin (:3001),
// so a bare relative src would 404 against the frontend origin (:3000). Prefix
// backendUrl for those; pass http(s)/blob/data URLs through unchanged.
export function assetUrl(url) {
    if (!url)
        return url !== null && url !== void 0 ? url : undefined;
    if (/^(https?:|blob:|data:)/.test(url))
        return url;
    return `${backendUrl}${url}`;
}
// Request a downscaled thumbnail variant of a backend-hosted image. The backend
// serves `/uploads/<id>?w=<px>` as a small WebP (see server.ts), so grid cards and
// map tiles transfer ~10-20 KB instead of the 100-400 KB originals — this is what
// makes the play picker load instantly instead of stalling for seconds.
//
// Resolves the reference like assetUrl first (stored form is a RELATIVE
// /uploads/<id>, served from the backend origin), so callers can pass a stored
// value directly. Only our own `/uploads/` URLs get the ?w= rewrite; external/CDN
// URLs (which we can't resize) and empty values pass through unchanged.
export function thumb(url, width) {
    const resolved = assetUrl(url);
    if (!resolved)
        return resolved;
    if (!resolved.includes('/uploads/'))
        return resolved;
    return `${resolved}${resolved.includes('?') ? '&' : '?'}w=${width}`;
}
// Read a picked file's true pixel size in the browser. Rejects on a file the browser
// can't decode; the caller turns that into the same message the server would give.
export function readImageDimensions(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            // naturalWidth/Height is the decoded size, unaffected by CSS.
            const dims = { width: img.naturalWidth, height: img.naturalHeight };
            URL.revokeObjectURL(url);
            resolve(dims);
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
        img.src = url;
    });
}
/**
 * Pre-flight the game's fixed art sizes before spending an upload. Returns null when
 * the file is fine (or the kind isn't gated), else the message to show the author.
 * The server re-checks the same rule from the SAME shared spec — this is only here so
 * the author is told instantly and by the picker they used, not by a failed request.
 */
export function checkGameImageSize(kind, file) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const { width, height } = yield readImageDimensions(file);
            return validateGameImageSize(kind, width, height);
        }
        catch (_a) {
            return 'Could not read this image — it may be corrupt or an unsupported format.';
        }
    });
}
